import { useEffect, useState, useRef, useCallback } from "react";
import { EditorView, lineNumbers, highlightActiveLine, keymap } from "@codemirror/view";
import { EditorState } from "@codemirror/state";
import { bracketMatching } from "@codemirror/language";
import { search, highlightSelectionMatches } from "@codemirror/search";
import { defaultKeymap } from "@codemirror/commands";
import { liliaEditorTheme, liliaHighlightStyle } from "@/lib/codemirror/editor-theme";
import { latexLanguage } from "@/lib/codemirror/latex-language";
import { syntaxHighlighting } from "@codemirror/language";
import { api } from "@/lib/api";
import { Button } from "@/components/lilia-ui/button";
import { useTranslation } from "@/utils/hooks/useTranslation";

interface SourceEditorProps {
  documentId: string;
  /** Bumped by EditorPage onSaveSuccess. Triggers a refetch + a
   *  transactional doc replace (not a full CodeMirror recreate),
   *  so the source pane reflects the editor without flicker.
   *  Per John's 2026-05-13 LaTeX-sync-and-save-UX report. */
  refreshKey?: number;
}

export function SourceEditor({ documentId, refreshKey = 0 }: SourceEditorProps) {
  const { t } = useTranslation('editor');
  const containerRef = useRef<HTMLDivElement>(null);
  const editorViewRef = useRef<EditorView | null>(null);
  const [latexContent, setLatexContent] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Fetch LaTeX content — refetches on refreshKey bump (post-save).
  // Doesn't blank the pane on subsequent refreshes (no isLoading flip);
  // the stale content stays visible until the new content lands.
  useEffect(() => {
    let cancelled = false;

    async function fetchLatex() {
      setError(null);
      try {
        const response = await api.preview.getLatex(documentId);
        if (!cancelled) {
          setLatexContent(response.content);
          setIsInitialLoad(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load LaTeX");
        }
      }
    }

    fetchLatex();
    return () => { cancelled = true; };
  }, [documentId, refreshKey]);

  // Initialize CodeMirror ONCE per document. On subsequent
  // `latexContent` changes, dispatch a doc-replace transaction so the
  // view diff-updates instead of recreating — no flicker, scroll
  // position preserved.
  useEffect(() => {
    if (!containerRef.current || latexContent === null) return;

    if (editorViewRef.current) {
      const view = editorViewRef.current;
      const cur = view.state.doc.toString();
      if (cur !== latexContent) {
        view.dispatch({
          changes: { from: 0, to: cur.length, insert: latexContent },
        });
      }
      return;
    }

    const state = EditorState.create({
      doc: latexContent,
      extensions: [
        lineNumbers(),
        highlightActiveLine(),
        bracketMatching(),
        search(),
        highlightSelectionMatches(),
        keymap.of(defaultKeymap),
        latexLanguage,
        syntaxHighlighting(liliaHighlightStyle),
        liliaEditorTheme,
        EditorView.editable.of(false),
        EditorView.lineWrapping,
      ],
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    editorViewRef.current = view;

    return () => {
      view.destroy();
      editorViewRef.current = null;
    };
  }, [latexContent]);

  const handleCopy = useCallback(async () => {
    if (latexContent) {
      await navigator.clipboard.writeText(latexContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [latexContent]);

  if (isInitialLoad) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
        <span>{t('loadingLatexSource')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center text-destructive text-sm">
        <span>{t('failedToLoadSource', { error })}</span>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-[var(--lilia-surface)] border-b border-[var(--lilia-border)] shrink-0">
        <span className="text-xs text-muted-foreground font-medium">{t('latexSourceReadOnly')}</span>
        <Button size="sm" variant="subtle" onClick={handleCopy}>
          {copied ? t('copied') : t('copyAll')}
        </Button>
      </div>
      <div className="flex-1 overflow-auto [&_.cm-editor]:h-full [&_.cm-scroller]:overflow-auto" ref={containerRef} />
    </div>
  );
}
